import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Users,
  Sparkles,
  Zap,
  Globe2,
  Swords,
  Bot,
  User,
  Crown,
  Shield,
  LogOut,
  Search,
  MessageSquare,
} from "lucide-react";
import Logo from "./Logo";
import NotificationBell from "./NotificationBell";
import { useAuth } from "../lib/AuthContext";
import { LanguageSwitcher, useT } from "../lib/i18n";
import useNavBadges from "../lib/useNavBadges";
import { Button } from "./ui/button";

const primary = (t) => [
  { to: "/app", label: t("nav.home"), icon: Home, tid: "nav-home" },
  { to: "/app/players", label: t("nav.players") || "Players", icon: Search, tid: "nav-players" },
  { to: "/app/teammates", label: t("nav.teammates"), icon: Users, tid: "nav-teammates" },
  { to: "/app/play", label: t("nav.play"), icon: Sparkles, tid: "nav-play" },
  { to: "/app/coach", label: t("nav.coach"), icon: Bot, tid: "nav-coach" },
];

const more = (t) => [
  { to: "/app/trends", label: t("nav.trends"), icon: Globe2, tid: "nav-trends" },
  { to: "/app/builds", label: t("nav.builds"), icon: Swords, tid: "nav-builds" },
  { to: "/app/fps-fix", label: t("nav.fps"), icon: Zap, tid: "nav-fps" },
  { to: "/app/premium", label: t("nav.premium"), icon: Crown, tid: "nav-premium" },
];

export function TopNav() {
  const { t } = useT();
  const { user, profile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isGuest = !user;

  const homeLink = isGuest ? "/" : "/app";

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10"
      data-testid="top-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Logo to={homeLink} />

        <nav className="hidden md:flex items-center gap-1">
          {[...primary(t), ...more(t)].map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.tid}
                className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                  active
                    ? "bg-neon/10 text-neon"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon size={16} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher className="hidden sm:inline-flex" />

          {isGuest ? (
            <>
              <Link to="/login" data-testid="top-signin">
                <Button
                  variant="ghost"
                  size="sm"
                  className="hidden sm:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-full"
                >
                  {t("nav.signin")}
                </Button>
              </Link>
              <Link to="/login" data-testid="top-signup">
                <Button
                  size="sm"
                  className="bg-neon text-black hover:bg-neon-hover rounded-full font-bold shadow-neon"
                >
                  {t("nav.signup")}
                </Button>
              </Link>
            </>
          ) : (
            <>
              <NotificationBell />
              <Link
                to="/app/squads"
                data-testid="nav-squads"
                title="Squads"
                className="hidden md:inline-flex items-center justify-center w-9 h-9 rounded-full text-zinc-300 hover:text-neon hover:bg-white/5 transition-colors"
              >
                <Swords size={16} />
              </Link>
              <Link
                to="/app/friends"
                data-testid="nav-friends"
                title="Friends"
                className="hidden md:inline-flex items-center justify-center w-9 h-9 rounded-full text-zinc-300 hover:text-neon hover:bg-white/5 transition-colors"
              >
                <Users size={16} />
              </Link>
              {user?.isAdmin && (
                <Link
                  to="/app/admin"
                  data-testid="nav-admin"
                  className="hidden sm:inline-flex items-center gap-1.5 text-xs text-neon px-3 py-1.5 rounded-full border border-neon/30 hover:bg-neon/10"
                >
                  <Shield size={14} />
                  {t("nav.admin")}
                </Link>
              )}
              <Link
                to="/app/profile"
                data-testid="nav-profile"
                className="w-9 h-9 rounded-full bg-gradient-to-br from-neon to-blue-500 grid place-items-center text-black font-bold"
                title={t("nav.profile")}
              >
                {(profile?.nickname || "P").slice(0, 1).toUpperCase()}
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                data-testid="logout-btn"
                className="hidden sm:inline-flex text-zinc-400 hover:text-white"
                aria-label={t("nav.signout")}
              >
                <LogOut size={16} />
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function BottomNav() {
  const { t } = useT();
  const { user } = useAuth();
  const location = useLocation();
  const isGuest = !user;
  const { dmUnread, squadPending } = useNavBadges();

  const items = isGuest
    ? [
        { to: "/", label: t("nav.home"), icon: Home, tid: "bottom-home" },
        { to: "/app/trends", label: t("nav.trends"), icon: Globe2, tid: "bottom-trends" },
        { to: "/app/play", label: t("nav.play"), icon: Sparkles, tid: "bottom-play" },
        { to: "/app/teammates", label: t("nav.teammates"), icon: Users, tid: "bottom-teammates" },
        { to: "/login", label: t("nav.signup"), icon: User, tid: "bottom-signup" },
      ]
    : [
        { to: "/app", label: t("nav.home"), icon: Home, tid: "nav-home" },
        { to: "/app/teammates", label: t("nav.teammates"), icon: Users, tid: "nav-teammates" },
        { to: "/app/squads", label: t("nav.squads") || "Squads", icon: Shield, tid: "nav-squads", badge: squadPending },
        { to: "/app/chat", label: t("nav.messages") || "Nachrichten", icon: MessageSquare, tid: "nav-messages", badge: dmUnread, matchPrefix: "/app/chat" },
        { to: "/app/coach", label: t("nav.coach"), icon: Bot, tid: "nav-coach" },
        { to: "/app/profile", label: t("nav.profile"), icon: User, tid: "nav-profile-bottom" },
      ];

  // Tailwind doesn't support dynamic grid-cols-N — use inline style so the
  // layout adapts cleanly to 5 (guest) or 6 (auth) tabs without flicker.
  const gridStyle = { gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` };

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-black/85 border-t border-white/10 pb-[env(safe-area-inset-bottom)]"
      data-testid="bottom-nav"
    >
      <ul className="grid px-1 py-2" style={gridStyle}>
        {items.map((item) => {
          const Icon = item.icon;
          const active = item.matchPrefix
            ? location.pathname.startsWith(item.matchPrefix)
            : location.pathname === item.to;
          const isCta = isGuest && item.to === "/login";
          const showBadge = !active && typeof item.badge === "number" && item.badge > 0;
          return (
            <li key={item.to} className="flex">
              <Link
                to={item.to}
                data-testid={item.tid}
                className={`relative flex-1 flex flex-col items-center gap-1 py-1.5 rounded-xl text-[10px] font-medium min-h-[48px] transition-colors ${
                  isCta
                    ? "text-neon"
                    : active
                    ? "text-neon bg-neon/10 shadow-[0_0_18px_-4px_rgba(34,211,238,0.45)]"
                    : "text-zinc-400 hover:text-zinc-200"
                }`}
              >
                <div className="relative">
                  <Icon size={20} className={active || isCta ? "text-neon" : ""} />
                  {showBadge && (
                    <span
                      data-testid={`${item.tid}-badge`}
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center rounded-full bg-neon text-black text-[10px] font-black leading-none shadow-[0_0_8px_rgba(34,211,238,0.7)]"
                    >
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  )}
                </div>
                <span className="truncate max-w-full px-0.5">{item.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** Sticky signup bar that appears under the top nav for guests on preview pages.
 *  Mobile: text-only (no duplicate CTA). Desktop: shows a secondary register button. */
export function GuestStickyBar() {
  const { user } = useAuth();
  const { t } = useT();
  if (user) return null;
  return (
    <div
      className="sticky top-16 z-30 bg-gradient-to-r from-neon/15 via-neon/10 to-transparent border-b border-neon/20 backdrop-blur-md"
      data-testid="guest-sticky-bar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full bg-neon animate-pulse shrink-0" />
          <p className="text-[12px] sm:text-sm text-zinc-200 truncate">{t("guest.sticky_cta")}</p>
        </div>
        <Link to="/login" data-testid="guest-sticky-cta" className="hidden sm:inline-flex">
          <Button
            size="sm"
            className="h-8 px-4 rounded-full bg-neon text-black hover:bg-neon-hover font-bold text-xs shadow-neon shrink-0"
          >
            {t("guest.sticky_cta_btn")}
          </Button>
        </Link>
      </div>
    </div>
  );
}
